import { type Observable } from '@legendapp/state';
import { createSyncedPersistedStore } from '@kidslabs/module-core';

import type { CartItemReference } from '../models/types/cart-item-reference';
import { CartItemReferenceSynchronizationService } from '../services/cart-item-reference-synchronization.service';
import { cartStoreAPI } from './cart-store.public-api';
import type { CartItemReferenceStore } from './cart-item-reference.store.types';
import { cartItemReferenceSynchronizationStore$ } from './cart-item-reference-synchronization.store';

/**
 * ============================================================================
 * CART ITEM REFERENCE STORE - DEBOUNCED SYNC WITH PENDING STATE QUEUE
 * ============================================================================
 *
 * ARCHITECTURE PRINCIPLE: Never Skip State Changes
 *
 * PROBLEM (OLD):
 * - If sync is in progress, skip new changes → STATE LOSS! ❌
 *
 * SOLUTION (NEW):
 * - If sync is in progress, QUEUE the latest state ✅
 * - After sync completes, sync the queued state ✅
 * - Use debounce timer to batch rapid changes ✅
 *
 * STRATEGY:
 * 1. Debounce: Batch rapid changes within 1500ms
 * 2. Pending Queue: If sync running, save latest state to queue
 * 3. Auto-drain Queue: After sync completes, sync queued state if any
 * 4. Smart Diff: Skip sync if no real changes
 *
 * WEB-SPECIFIC IMPLEMENTATION (MATCHING MOBILE PATTERN):
 * - Uses IndexedDB for persistence (via createSyncedPersistedStore)
 * - Uses set callback to trigger sync (same as mobile's syncPluginStore)
 * - Safe for SSR (no timers on server-side)
 *
 * CONFIGURATION:
 * - debounceMs: 1500ms (batch rapid changes)
 * - maxWaitMs: 5000ms (force sync after max wait)
 * - enableSmartDiff: true (skip if no changes)
 * - enablePendingQueue: true (never lose state changes)
 */

// ════════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ════════════════════════════════════════════════════════════════════════════

/**
 * Debounced sync configuration
 *
 * The sync mechanism uses both debounce AND max wait:
 * - debounceMs: Wait this long after LAST change before syncing
 * - maxWaitMs: Force sync after this time even if changes keep coming
 *
 * Example flow with debounceMs=1500, maxWaitMs=5000:
 * 1. User changes quantity at t=0 → Start debounce timer (1500ms) + max wait timer (5000ms)
 * 2. User changes again at t=1000 → Reset debounce timer (1500ms from t=1000)
 * 3. User stops changing → Debounce fires at t=2500 → SYNC
 *
 * Alternative flow (rapid changes):
 * 1. User keeps changing every 1s: t=0, t=1000, t=2000, t=3000, t=4000, t=5000
 * 2. Max wait fires at t=5000 → FORCE SYNC (even though debounce hasn't fired)
 */
const SYNC_CONFIG = {
    /**
     * Debounce delay - wait this long after LAST change
     * Set to 800ms matching mobile for responsive UX
     */
    debounceMs: 800,

    /**
     * Max wait - force sync after this time even if changes keep coming
     * Set to 3000ms matching mobile to prevent long delays
     */
    maxWaitMs: 3000,

    /**
     * Enable smart diff - only sync if items actually changed
     * This prevents unnecessary API calls when user toggles selection on/off
     */
    enableSmartDiff: true,

    /**
     * Enable pending queue - queue state changes while sync is in progress
     * After current sync completes, drain the queue and sync pending state
     */
    enablePendingQueue: true,
};

// ════════════════════════════════════════════════════════════════════════════
// STATE TRACKING
// ════════════════════════════════════════════════════════════════════════════

/**
 * Last successfully synced state
 * Used for smart diff to detect if items actually changed
 */
let lastSyncedState: CartItemReference[] = [];

/**
 * Pending state queue
 * When sync is in progress and user makes more changes,
 * queue the latest state to sync after current sync completes
 */
let pendingState: CartItemReference[] | null = null;

// ════════════════════════════════════════════════════════════════════════════
// PENDING QUANTITY CHANGES TRACKER
// ════════════════════════════════════════════════════════════════════════════

/**
 * Tracks per-item pending quantity changes that have NOT yet been synced to server.
 *
 * When the user rapidly clicks +/- buttons, the local store is updated immediately
 * (optimistic update), but the server sync is debounced. During this window,
 * any server response (from a previous sync or fetchCartItem) must NOT overwrite
 * the user's latest local quantity.
 *
 * Key: cartItemReferenceId
 * Value: { latestQuantity: number, timestamp: number }
 */
const pendingQuantityChanges = new Map<string, { latestQuantity: number; timestamp: number }>();

/**
 * Mark a cart item reference as having a pending (unsynchronized) quantity change.
 * Call this when the user changes quantity locally before the server sync fires.
 */
export function markQuantityPending(cartItemReferenceId: string, latestQuantity: number): void {
    pendingQuantityChanges.set(cartItemReferenceId, {
        latestQuantity,
        timestamp: Date.now(),
    });
    console.log('[CartItemReferenceStore] Marked pending quantity change', {
        cartItemReferenceId,
        latestQuantity,
        pendingCount: pendingQuantityChanges.size,
    });
}

/**
 * Clear pending quantity change for a specific item.
 * Call this after the server has successfully accepted the quantity.
 */
export function clearQuantityPending(cartItemReferenceId: string): void {
    pendingQuantityChanges.delete(cartItemReferenceId);
}

/**
 * Clear ALL pending quantity changes.
 * Call this after a full sync completes successfully.
 */
export function clearAllPendingQuantityChanges(): void {
    if (pendingQuantityChanges.size > 0) {
        console.log('[CartItemReferenceStore] Clearing all pending quantity changes', {
            count: pendingQuantityChanges.size,
        });
    }
    pendingQuantityChanges.clear();
}

/**
 * Check if a specific item has a pending (unsynchronized) quantity change.
 */
export function hasPendingQuantityChange(cartItemReferenceId: string): boolean {
    return pendingQuantityChanges.has(cartItemReferenceId);
}

/**
 * Get the pending quantity for an item, or undefined if no pending change.
 */
export function getPendingQuantity(cartItemReferenceId: string): number | undefined {
    return pendingQuantityChanges.get(cartItemReferenceId)?.latestQuantity;
}

/**
 * Check if ANY items have pending quantity changes.
 */
export function hasAnyPendingQuantityChanges(): boolean {
    return pendingQuantityChanges.size > 0;
}

// ════════════════════════════════════════════════════════════════════════════
// TODO-4.7: LAST USER ACTION TIMESTAMP
// ════════════════════════════════════════════════════════════════════════════

/**
 * Tracks the timestamp of the last user-initiated action (qty change, toggle
 * selection, item removal). Used by useHybridCartPrices to reject server data
 * that was fetched BEFORE this timestamp — such data reflects the old cart
 * state and must not overwrite the new local state.
 */
let lastUserActionTimestamp = 0;

/**
 * Mark that a user action just occurred.
 * Call this in every user-initiated cart mutation (increase/decrease qty,
 * toggle selection, remove item).
 */
export function markUserAction(): void {
    lastUserActionTimestamp = Date.now();
}

/**
 * Get the timestamp of the last user action.
 * Used by useHybridCartPrices to guard against stale server responses.
 */
export function getLastUserActionTimestamp(): number {
    return lastUserActionTimestamp;
}

/**
 * Track first change for max wait timer calculation
 */
let firstChangeTimestamp: number | null = null;

// ════════════════════════════════════════════════════════════════════════════
// SYNC LOCK - DEFAULT LOCKED UNTIL SERVER DATA ARRIVES
// ════════════════════════════════════════════════════════════════════════════

/**
 * Sync is LOCKED by default on page load / module initialization.
 *
 * This prevents Legend-State from syncing hydrated (potentially stale/empty)
 * data to server before we have fetched the authoritative server state.
 *
 * The lock is released ONLY when fetchCartItemInternal (server fetch)
 * completes successfully. After that, normal user-driven sync resumes.
 *
 * This single mechanism solves:
 * - Store switch race condition (clear → hydrate [] → sync [] to server)
 * - Normal page load race condition (hydrate old data → sync before fetch)
 * - Any scenario where local data diverges from server before first fetch
 */
let isSyncLocked = true;

/**
 * Unlock sync after server data has been fetched and reconciled.
 * Call this after fetchCartItemInternal completes (success or error).
 */
export function unlockSync(): void {
    if (isSyncLocked) {
        isSyncLocked = false;
        console.log('[CartItemReferenceStore] Sync UNLOCKED — server data received, auto-sync enabled');
    }
}

/**
 * Re-lock sync. Called during store code change to prevent
 * sync of cleared data before page reload.
 */
export function lockSync(): void {
    isSyncLocked = true;
    clearAllTimers();
    console.log('[CartItemReferenceStore] Sync LOCKED — waiting for server data');
}

/**
 * Check if sync is currently locked.
 */
export function isSyncCurrentlyLocked(): boolean {
    return isSyncLocked;
}

/**
 * Flag to prevent onChange from triggering during internal updates
 * (e.g., when reconciliation updates items after sync)
 */
let isInternalUpdate = false;

// NOTE: isHydrated guard was removed — isSyncLocked fully covers this case.
// The hydration onChange is blocked by isSyncLocked (true by default).
// unlockSync() is only called after fetchCartItemInternal completes,
// so no stale hydrated data can ever reach the server.

// ════════════════════════════════════════════════════════════════════════════
// DEBOUNCE TIMERS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Debounce timer - fires after user stops changing
 */
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Max wait timer - fires after maxWaitMs even if user keeps changing
 */
let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;

// ════════════════════════════════════════════════════════════════════════════
// SMART DIFF HELPERS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Check if items have real changes compared to last synced state
 * Uses a simple comparison based on item IDs, quantities, selection status, and logics
 */
function hasRealChanges(oldItems: CartItemReference[], newItems: CartItemReference[]): boolean {
    if (oldItems.length !== newItems.length) {
        return true;
    }

    // Create a map for quick lookup
    const oldMap = new Map(oldItems.map(item => [item.cartItemReferenceId, item]));

    for (const newItem of newItems) {
        const oldItem = oldMap.get(newItem.cartItemReferenceId);
        if (!oldItem) {
            return true; // New item added
        }
        if (oldItem.qty !== newItem.qty) {
            return true; // Quantity changed
        }
        if (oldItem.isSelected !== newItem.isSelected) {
            return true; // Selection changed
        }
        // Check for logics changes (for gift selection, etc.)
        if (JSON.stringify(oldItem.logics) !== JSON.stringify(newItem.logics)) {
            return true;
        }
    }

    return false;
}

// ════════════════════════════════════════════════════════════════════════════
// TIMER MANAGEMENT
// ════════════════════════════════════════════════════════════════════════════

/**
 * Clear debounce timer
 */
function clearDebounceTimer(): void {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
    }
}

/**
 * Clear max wait timer
 */
function clearMaxWaitTimer(): void {
    if (maxWaitTimer) {
        clearTimeout(maxWaitTimer);
        maxWaitTimer = null;
    }
    firstChangeTimestamp = null;
}

/**
 * Clear all timers
 */
function clearAllTimers(): void {
    clearDebounceTimer();
    clearMaxWaitTimer();
}

// ════════════════════════════════════════════════════════════════════════════
// SYNC EXECUTION WITH PENDING QUEUE DRAIN
// ════════════════════════════════════════════════════════════════════════════

/**
 * Execute sync to server with automatic pending queue drain
 *
 * CRITICAL: After sync completes, check if there's pending state in queue.
 * If yes, sync the pending state (this ensures no state changes are lost).
 *
 * @param items - Cart item references to sync
 */
export async function executeSyncToServer(items: CartItemReference[], explicitCartId?: string): Promise<void> {
    // CRITICAL: Check if sync is already in progress
    if (CartItemReferenceSynchronizationService.isInProgress()) {
        console.log('[CartItemReferenceStore] Sync already in progress, cannot start new sync');
        return;
    }

    // Resolve cart ID: prefer explicit parameter, fall back to store
    const resolvedCartId = explicitCartId || cartStoreAPI.state.getCartId();
    if (!resolvedCartId) {
        console.warn('[CartItemReferenceStore] No cart ID available — skipping sync');
        return;
    }

    console.log('[CartItemReferenceStore] Starting sync execution', {
        itemCount: items.length,
        hasPendingState: !!pendingState,
    });

    try {
        // Execute sync
        const service = new CartItemReferenceSynchronizationService();
        await service.sync(resolvedCartId, items, () => {
            // ════════════════════════════════════════════════════════════════
            // CRITICAL FIX: Update lastSyncedState BEFORE applyStoreMutations.
            // ════════════════════════════════════════════════════════════════
            // This callback runs AFTER the API call succeeds but BEFORE
            // setInternalUpdate(true) + store writes in applyStoreMutations.
            // At this point isCurrentlySyncing is still true, so handleStoreChange
            // will queue any concurrent changes instead of starting a new sync.
            //
            // Previously, lastSyncedState was updated AFTER applyStoreMutations
            // (and after setInternalUpdate(false)). This created a timing gap:
            // 1. applyStoreMutations sets setInternalUpdate(false)
            // 2. Legend-State onChange fires with isInternalUpdate=false
            // 3. handleStoreChange checks hasRealChanges(oldLastSyncedState, current) → TRUE
            // 4. Spurious re-sync triggered → selection bounce-back!
            //
            // By updating lastSyncedState here, step 3 correctly returns FALSE
            // because the baseline now matches what the server accepted.
            lastSyncedState = [...items];
        });

        // Clear pending quantity changes - server now has the correct quantities
        clearAllPendingQuantityChanges();

        // Bump version so reactive consumers (e.g. cart-level gifts) know a sync just finished
        const prev = cartItemReferenceSynchronizationStore$.syncVersion.peek() ?? 0;
        cartItemReferenceSynchronizationStore$.syncVersion.set(prev + 1);

        console.log('[CartItemReferenceStore] Sync completed successfully');

        // ════════════════════════════════════════════════════════════════════
        // CRITICAL: Drain pending queue if any
        // ════════════════════════════════════════════════════════════════════
        // If state changed while we were syncing, sync the pending state now
        if (SYNC_CONFIG.enablePendingQueue && pendingState) {
            const stateToDrain = pendingState;
            pendingState = null; // Clear queue

            // Check if pending state is different from what we just synced
            if (hasRealChanges(items, stateToDrain)) {
                console.log('[CartItemReferenceStore] Draining pending queue with new state', {
                    pendingCount: stateToDrain.length,
                });

                // Recursively sync the pending state
                // This is safe because:
                // 1. We cleared pendingState, so new changes will queue again
                // 2. Recursive depth is limited by user interaction speed
                await executeSyncToServer(stateToDrain, resolvedCartId);
            } else {
                console.log('[CartItemReferenceStore] Pending state same as synced, skipping drain');
            }
        } else {
            // ════════════════════════════════════════════════════════════════
            // POST-SYNC DIVERGENCE CHECK
            // ════════════════════════════════════════════════════════════════
            // Even if pendingState is null, the store may have diverged
            // during the sync (e.g., user removed all items while sync was
            // in-flight, and applyStoreMutations skipped the stale update).
            // Re-read current state and schedule a sync if needed.
            const currentState = cartItemReferenceStore$.peek();
            const currentItems = (currentState.items as CartItemReference[]) ?? [];
            if (hasRealChanges(items, currentItems)) {
                console.log('[CartItemReferenceStore] Post-sync divergence detected, scheduling new sync', {
                    syncedCount: items.length,
                    currentCount: currentItems.length,
                });
                // Use a short delay to allow any remaining internal updates to settle
                setTimeout(() => {
                    handleStoreChange(currentItems);
                }, 100);
            }
        }
    } catch (error) {
        // ════════════════════════════════════════════════════════════════════
        // ERROR RECOVERY: Distinguish recoverable vs unrecoverable errors
        // ════════════════════════════════════════════════════════════════════
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isGiftRelatedError =
            errorMessage.includes('GIFT_NOT_SALABLE') ||
            errorMessage.includes('GIFT_INSUFFICIENT_STOCK') ||
            errorMessage.includes('quà tặng') ||
            errorMessage.includes('gift');

        if (isGiftRelatedError) {
            // Recoverable: Gift product unavailable — strip invalid gift actions and retry once
            console.warn(
                '[CartItemReferenceStore] Gift-related sync error detected, attempting recovery:',
                errorMessage
            );

            /**
                @deprecated don't handle this case
                try {
                    // Strip all gift actions from references to allow base cart items to sync
                    const cleanedItems = items.map(item => ({
                        ...item,
                        logics: (item.logics ?? []).map(logic => ({
                            ...logic,
                            actions: (logic.actions ?? []).filter(
                                (action: { actionType?: string }) => action.actionType !== 'free_gift'
                            ),
                        })),
                    }));

                    const service = new CartItemReferenceSynchronizationService();
                    await service.sync(cartStoreAPI.state.getCartId(), cleanedItems);

                    // Recovery successful — update synced state
                    lastSyncedState = [...cleanedItems];

                    const prev = cartItemReferenceSynchronizationStore$.syncVersion.peek() ?? 0;
                    cartItemReferenceSynchronizationStore$.syncVersion.set(prev + 1);

                    console.log('[CartItemReferenceStore] Gift error recovery successful — invalid gifts stripped');
                } catch (retryError) {
                    console.error('[CartItemReferenceStore] Gift error recovery also failed:', retryError);
                    throw retryError;
                }
             */
        } else {
            // Unrecoverable: network error, auth error, etc. — propagate
            console.error('[CartItemReferenceStore] Sync failed (unrecoverable):', error);
            throw error;
        }
    }
}

// ════════════════════════════════════════════════════════════════════════════
// DEBOUNCED SYNC HANDLER (WEB-SPECIFIC)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Handle store change with debounced sync
 *
 * This function is called whenever the store's items change.
 * It implements the same debounce + max wait + pending queue logic as mobile.
 *
 * WEB-SPECIFIC: This is triggered via onChange listener instead of sync callback.
 *
 * @param currentItems - Current cart item references from store
 */
function handleStoreChange(currentItems: CartItemReference[]): void {
    // ════════════════════════════════════════════════════════════════════
    // SYNC LOCK: Block ALL outbound sync until server data arrives.
    // Default locked on page load, unlocked after fetchCartItemInternal.
    // ════════════════════════════════════════════════════════════════════
    if (isSyncLocked) {
        console.log('[CartItemReferenceStore] Skipping sync — sync locked (waiting for server data)');
        return;
    }

    console.log('[CartItemReferenceStore] Store change detected', {
        itemCount: currentItems.length,
        syncInProgress: CartItemReferenceSynchronizationService.isInProgress(),
        hasPendingState: !!pendingState,
        isInternalUpdate,
    });

    // Skip if this is an internal update (e.g., reconciliation after sync)
    if (isInternalUpdate) {
        console.log('[CartItemReferenceStore] Skipping sync - internal update');
        return;
    }

    // ════════════════════════════════════════════════════════════════════
    // STEP 1: Smart Diff - Skip if no real changes
    // ════════════════════════════════════════════════════════════════════
    if (SYNC_CONFIG.enableSmartDiff) {
        const hasChanges = hasRealChanges(lastSyncedState, currentItems);

        if (!hasChanges) {
            console.log('[CartItemReferenceStore] No real changes detected, skipping sync');
            return;
        }
    }

    // ════════════════════════════════════════════════════════════════════
    // STEP 2: Check if sync is in progress
    // ════════════════════════════════════════════════════════════════════
    if (CartItemReferenceSynchronizationService.isInProgress()) {
        // ✅ CRITICAL FIX: Queue the state instead of skipping!
        if (SYNC_CONFIG.enablePendingQueue) {
            console.log('[CartItemReferenceStore] Sync in progress, queuing state to pending');
            pendingState = [...currentItems]; // Save latest state to queue
        }

        // Return without syncing - pending state will be drained after current sync
        return;
    }

    // ════════════════════════════════════════════════════════════════════
    // STEP 3: Debounce Logic - Batch rapid changes
    // ════════════════════════════════════════════════════════════════════

    // Track first change for max wait timer
    if (firstChangeTimestamp === null) {
        firstChangeTimestamp = Date.now();
    }

    // Clear existing timers (debounce behavior)
    clearDebounceTimer();
    clearMaxWaitTimer();

    // Calculate time until max wait
    const elapsedSinceFirstChange = Date.now() - firstChangeTimestamp;
    const timeUntilMaxWait = Math.max(0, SYNC_CONFIG.maxWaitMs - elapsedSinceFirstChange);

    // ════════════════════════════════════════════════════════════════════
    // TRUE DEBOUNCE: Only sync AFTER user stops changing
    // ════════════════════════════════════════════════════════════════════
    // Set debounce timer (fires after user stops changing for 1500ms)
    debounceTimer = setTimeout(async () => {
        console.log('[CartItemReferenceStore] ✅ Debounce timer fired - user stopped changing');

        // ✅ CRITICAL: Clear ALL timers to prevent duplicate sync
        // If we don't clear maxWait timer, it will fire later and sync AGAIN!
        clearAllTimers();

        // ✅ CRITICAL: Read LATEST state from store, not closure variable!
        const latestState = cartItemReferenceStore$.peek();
        const latestItems = (latestState.items as CartItemReference[]) ?? [];

        console.log('[CartItemReferenceStore] 🚀 Syncing with LATEST state (from debounce)', {
            itemCount: latestItems.length,
        });

        await executeSyncToServer(latestItems);
    }, SYNC_CONFIG.debounceMs);

    // ════════════════════════════════════════════════════════════════════
    // MAX WAIT TIMER: Force sync if user keeps changing for too long
    // ════════════════════════════════════════════════════════════════════
    // Set max wait timer (fires after 5s even if user keeps changing)
    if (timeUntilMaxWait > 0) {
        maxWaitTimer = setTimeout(async () => {
            console.log('[CartItemReferenceStore] ⏰ Max wait timer fired - forcing sync');

            // ✅ CRITICAL: Clear ALL timers to prevent duplicate sync
            clearAllTimers();

            // ✅ CRITICAL: Read LATEST state from store
            const latestState = cartItemReferenceStore$.peek();
            const latestItems = (latestState.items as CartItemReference[]) ?? [];

            console.log('[CartItemReferenceStore] 🚀 Force syncing with LATEST state (from maxWait)', {
                itemCount: latestItems.length,
            });

            await executeSyncToServer(latestItems);
        }, timeUntilMaxWait);
    }

    console.log('[CartItemReferenceStore] Timers set', {
        debounceMs: SYNC_CONFIG.debounceMs,
        maxWaitMs: timeUntilMaxWait,
    });
}

// ════════════════════════════════════════════════════════════════════════════
// STORE INITIALIZATION (WITH SET CALLBACK - LIKE MOBILE)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Cart Item Reference store with automatic IndexedDB persistence and sync callback
 *
 * ✅ Auto-registers as 'cartItemReference'
 * ✅ No manual SSR checks needed
 * ✅ Automatic persistence on client-side
 * ✅ Automatic debounced sync to server (via set callback - LIKE MOBILE!)
 *
 * NOTE: Using createSyncedPersistedStore with set callback ensures that
 * Legend-State properly triggers sync when store changes, matching mobile behavior.
 */
export const cartItemReferenceStore$: Observable<CartItemReferenceStore> =
    createSyncedPersistedStore<CartItemReferenceStore>({
        persist: {
            name: 'cartItemReference',
        },
        initial: {
            id: 'cart-item-reference-store',
            items: [],
            quantityStepper: 1,
        },
        // ═══════════════════════════════════════════════════════════════════
        // SET CALLBACK - Triggered by Legend-State when store changes
        // This is the same pattern as mobile's syncPluginStore
        // ═══════════════════════════════════════════════════════════════════
        set: async ({ value }): Promise<void> => {
            // Skip if running on server (SSR safety)
            if (typeof window === 'undefined') {
                return;
            }

            const items = (value?.items as CartItemReference[]) ?? [];
            handleStoreChange(items);
        },
        // NOTE: No debounceSet here - debouncing is handled by handleStoreChange
        // which has more sophisticated logic (maxWait timer, pending queue, smart diff)
    });

// ════════════════════════════════════════════════════════════════════════════
// PUBLIC API - INTERNAL UPDATE FLAG
// ════════════════════════════════════════════════════════════════════════════

/**
 * Set internal update flag
 *
 * Use this to temporarily disable sync when updating store from reconciliation.
 * This prevents circular sync triggers.
 *
 * @param value - Whether internal update is in progress
 */
export function setInternalUpdate(value: boolean): void {
    isInternalUpdate = value;
    if (value) {
        // Clear scheduled timers to prevent stale auto-syncs from firing
        // while internal (server-authoritative) writes are in progress.
        clearAllTimers();
        // ════════════════════════════════════════════════════════════════
        // CRITICAL FIX: Do NOT clear pendingState here!
        // ════════════════════════════════════════════════════════════════
        // Previously, pendingState was set to null here. This caused a
        // bug: when user clears all cart items during an in-flight sync,
        // the empty state [] is queued as pendingState. Then when the
        // sync completes and applyStoreMutations calls setInternalUpdate(true),
        // it destroyed that pending empty state. The follow-up drain never
        // happened, and the stale server data (with old items) persisted.
        //
        // pendingState is now only consumed (and cleared) by executeSyncToServer
        // when it drains the queue after a successful sync.
    }
}

/**
 * Execute a function with internal update flag set
 *
 * Convenience wrapper that ensures the flag is reset even if function throws.
 *
 * @param fn - Function to execute
 */
export async function withInternalUpdate<T>(fn: () => T | Promise<T>): Promise<T> {
    const previousValue = isInternalUpdate;
    isInternalUpdate = true;
    // Also clear timers to prevent stale auto-syncs from before this call
    clearAllTimers();
    // CRITICAL FIX: Do NOT clear pendingState here (same reason as setInternalUpdate)
    try {
        return await fn();
    } finally {
        isInternalUpdate = previousValue;
    }
}

// ════════════════════════════════════════════════════════════════════════════
// FORCE SYNC & STATUS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Force immediate sync (for checkout / buy-now).
 * Clears all timers and pending state, waits for any in-progress sync
 * to complete, then reads the absolute latest store state and syncs.
 *
 * RACE CONDITION FIX: previously, if a debounced auto-sync was already
 * in progress, `executeSyncToServer` would see `isInProgress() === true`
 * and return immediately — silently dropping the force sync.
 * Now we poll until the in-progress sync finishes, then start ours.
 */
export async function forceCartItemReferenceSync(explicitCartId?: string): Promise<void> {
    console.log('[CartItemReferenceStore] Force sync requested');

    // Step 1: Clear all debounce/maxWait timers to prevent scheduled auto-syncs
    clearAllTimers();

    // Step 2: Clear pending state so the in-progress sync does not drain stale data
    pendingState = null;

    // Step 3: Wait for any in-progress sync to complete
    const MAX_WAIT_MS = 5000;
    const POLL_INTERVAL_MS = 100;
    const startTime = Date.now();

    while (CartItemReferenceSynchronizationService.isInProgress()) {
        if (Date.now() - startTime > MAX_WAIT_MS) {
            console.warn('[CartItemReferenceStore] Force sync: timed out waiting for in-progress sync');
            break;
        }

        // Keep clearing pendingState each iteration to prevent the
        // finishing auto-sync from draining stale selection data
        pendingState = null;

        await new Promise<void>(resolve => {
            setTimeout(resolve, POLL_INTERVAL_MS);
        });
    }

    // Step 4: Read the ABSOLUTE LATEST state (after all selection changes)
    const currentState = cartItemReferenceStore$.peek();

    console.log('current state -> ', currentState.items);

    await executeSyncToServer(currentState.items as CartItemReference[], explicitCartId);
}

/**
 * Update the last-synced baseline state.
 *
 * Called after server-authoritative data is written to the store
 * (e.g. after fetchCartItemInternal reconciles server references)
 * so that subsequent user changes are correctly diffed against
 * the current server state, not an empty/stale baseline.
 */
export function updateLastSyncedState(items: CartItemReference[]): void {
    lastSyncedState = [...items];
}

/**
 * Get sync status
 */
export function getCartItemReferenceSyncStatus() {
    const currentState = cartItemReferenceStore$.peek();
    const hasPendingChanges = hasRealChanges(lastSyncedState, currentState.items as CartItemReference[]);

    return {
        hasPendingChanges,
        pendingCount: hasPendingChanges ? currentState.items?.length || 0 : 0,
        lastSyncedCount: lastSyncedState.length,
    };
}
